import {match} from 'ts-pattern';

import {useListPermissionExpressionQuery} from '@dms/api/security';
import {permissions, useIsLicenceEnabled, usePermissions} from '@dms/shared';

export type CustomerInsightsEntitlementStatus = 'loading' | 'ready' | 'error';

export interface CustomerInsightsEntitlements {
  status: CustomerInsightsEntitlementStatus;
  retry: VoidFunction;
  interests: boolean;
  businessCases: boolean;
  businessCaseSales: boolean;
  businessCasePurchases: boolean;
  serviceCases: boolean;
  invoices: boolean;
  tasks: boolean;
  communication: boolean;
}

export function useCustomerInsightsEntitlements(): CustomerInsightsEntitlements {
  const {isSalesModuleEnabled, isWorkshopModuleEnabled} = useIsLicenceEnabled();
  const permissionExpressionQuery = useListPermissionExpressionQuery();
  const {data: permissionExpressions} = permissionExpressionQuery;
  const [
    interestRead,
    businessCaseRead,
    viewBusinessCaseSelling,
    viewBusinessCasePurchase,
    serviceCaseRead,
    readInvoice,
    readTask,
    syncedMessageRead,
  ] = usePermissions({
    permissionKeys: [
      'interestRead',
      'businessCaseRead',
      'viewBusinessCaseSelling',
      'viewBusinessCasePurchase',
      'serviceCaseRead',
      'readInvoice',
      'readTask',
      'syncedMessageRead',
    ],
    scopes: {
      interestRead: {
        // Customer insights must show customer-wide Interests regardless of participation.
        participation: 'SKIP',
        // A customer has no branch, matching Customer Detail's Interest visibility.
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        // Customer Insights is customer-wide; the Data Grid enforces participation ACL.
        participation: 'SKIP',
        // A customer has no branch; the Data Grid enforces branch ACL.
        branchId: 'SKIP',
      },
      viewBusinessCasePurchase: {
        // Customer Insights is customer-wide; the Data Grid enforces participation ACL.
        participation: 'SKIP',
        // A customer has no branch; the Data Grid enforces branch ACL.
        branchId: 'SKIP',
      },
      serviceCaseRead: {
        // A customer has no branch.
        branchId: 'SKIP',
        // A customer has no Service Case type.
        serviceCaseType: 'SKIP',
      },
    },
  });
  const invoicePermissionExpression = permissionExpressions?.actions.find(
    (permission) =>
      permission.actionId === permissions.readInvoice.actionId &&
      permission.resourceId === permissions.readInvoice.resourceId
  );
  const canReadInvoices =
    invoicePermissionExpression === undefined
      ? readInvoice === true
      : invoicePermissionExpression.permissionsExpression.result !== 'DISALLOW';
  const canReadBusinessCases = isSalesModuleEnabled && businessCaseRead === true;
  const businessCaseSales = canReadBusinessCases && viewBusinessCaseSelling === true;
  const businessCasePurchases = canReadBusinessCases && viewBusinessCasePurchase === true;
  const status = match({
    isError: permissionExpressionQuery.isError,
    isLoading: permissionExpressionQuery.isLoading,
    isSuccess: permissionExpressionQuery.isSuccess,
  })
    .with({isError: true}, () => 'error' as const)
    .with({isLoading: true}, () => 'loading' as const)
    .with({isSuccess: true}, () => 'ready' as const)
    .otherwise(() => 'loading' as const);

  return {
    status,
    retry: permissionExpressionQuery.refetch,
    interests: interestRead === true,
    businessCases: businessCaseSales || businessCasePurchases,
    businessCaseSales,
    businessCasePurchases,
    serviceCases: isWorkshopModuleEnabled && serviceCaseRead === true,
    // The invoice Data Grid enforces row-level branch ACL. Here we only suppress the request when
    // the permission is globally disallowed; INVOICE.BRANCH is intentionally not evaluable by
    // usePermissions without a concrete invoice branch.
    invoices: canReadInvoices,
    tasks: readTask === true,
    communication: syncedMessageRead === true,
  };
}
